import { useGetIdentity, usePermissions } from '@refinedev/core';
import { hasPermission } from '../../../utils/permissions';
import type { UserIdentity } from '../../../providers/authProvider';

/**
 * True when the person manages roles only through customer:roles:* (no admin:roles:write).
 * The auth service then lets them see, create and edit customer-scoped roles only, so the
 * forms lock the scope to Customer.
 */
export function isCustomerRolesOnly(permissions: string[] | undefined): boolean {
  return !hasPermission(permissions ?? [], 'admin:roles:write');
}

export interface RoleAccess {
  /** False until the person's permissions have loaded; forms wait for it before choosing a scope. */
  loaded: boolean;
  customerOnly: boolean;
  /**
   * For a customer role manager limited to some companies: those company ids. They can't make
   * shared roles or roles for other companies. Null when there is no such limit.
   */
  limitedCompanies: string[] | null;
  /** A customer role manager whose company list couldn't be read: show an error, not empty choices. */
  identityError: boolean;
  /** Try loading the company list again. */
  retryIdentity: () => void;
  canDelete: boolean;
  canOpenUsers: boolean;
  canOpenPermission: boolean;
  /** Whether the person may put this key on a role (a customer role manager only keys they hold). */
  canGrant: (key: string) => boolean;
}

export function getRoleAccess(
  permissions: string[] | undefined,
  customerScope: string[] | null | undefined,
  identityKnown = true,
): RoleAccess {
  const perms = permissions ?? [];
  const customerOnly = isCustomerRolesOnly(perms);
  // If we couldn't read who they are, a customer role manager gets no companies at all (fail closed).
  const limitedCompanies = !customerOnly
    ? null
    : !identityKnown
      ? []
      : Array.isArray(customerScope) && customerScope.length > 0
        ? customerScope
        : null;
  return {
    loaded: permissions !== undefined,
    customerOnly,
    limitedCompanies,
    identityError: customerOnly && !identityKnown,
    retryIdentity: () => {},
    canDelete: hasPermission(perms, 'admin:roles:*'),
    canOpenUsers: hasPermission(perms, 'admin:users:read'),
    canOpenPermission: hasPermission(perms, 'permissions:read'),
    canGrant: (key: string) => !customerOnly || hasPermission(perms, key),
  };
}

/** What the signed-in person can do on the role pages. */
export function useRoleAccess(): RoleAccess {
  const { data: permissions, isLoading } = usePermissions<string[]>();
  const {
    data: identity,
    isLoading: identityLoading,
    refetch: refetchIdentity,
  } = useGetIdentity<UserIdentity>();
  const access = {
    ...getRoleAccess(isLoading ? undefined : permissions, identity?.customerScope, !!identity),
    retryIdentity: () => {
      void refetchIdentity?.();
    },
  };
  // Wait for the company list too, so a limited manager never briefly sees unlimited choices.
  // While it loads there is no error to show yet.
  return identityLoading ? { ...access, loaded: false, identityError: false } : access;
}

/**
 * For a customer role manager limited to some companies: whether they can save this role at all
 * (it must belong to one of their companies; shared customer roles are out of reach).
 */
export function canSaveInCompanyLimit(
  role: { companyId?: string | null },
  limitedCompanies: string[] | null,
): boolean {
  if (!limitedCompanies) return true;
  return !!role.companyId && limitedCompanies.includes(role.companyId);
}
